import { z } from "zod";

export * from "../credentials";
export * from "../repositories";
export * from "../integrations";
export * from "../connections";
export * from "../organisations";

/**
 * Declared permissions for the GitHub App integration.
 * The App requires contents:write, pull_requests:write, and metadata:read.
 */
export const gitHubAppPermissions = {
  contents: "write",
  pullRequests: "write",
  metadata: "read",
} as const;

export const gitHubAppPermissionsSchema = z.object({
  contents: z.literal("write"),
  pullRequests: z.literal("write"),
  metadata: z.literal("read"),
});
export type GitHubAppPermissions = z.infer<typeof gitHubAppPermissionsSchema>;

/**
 * Status of the GitHub App connection for a project or workspace.
 * When the GitHub App is not configured or disabled on the server,
 * status is "not-configured" and flow initiation is rejected.
 */
export const gitHubConnectionStatusSchema = z.enum([
  "not-configured",
  "pending-install",
  "connected",
  "disabled",
]);
export type GitHubConnectionStatus = z.infer<typeof gitHubConnectionStatusSchema>;

/**
 * GitHub App connection status payload returned by the server.
 */
export const gitHubConnectionStateSchema = z.object({
  status: gitHubConnectionStatusSchema,
  configured: z.boolean(),
  appName: z.string().nullable(),
  installUrl: z.string().nullable(),
  permissions: gitHubAppPermissionsSchema,
  installedRepositoriesCount: z.number().int().nonnegative(),
});
export type GitHubConnectionState = z.infer<typeof gitHubConnectionStateSchema>;

/**
 * Payment connection status for a project. Live stays off until a concrete provider is wired;
 * test mode stands in behind an explicit flag so checkout can be exercised without money moving.
 */
export const paymentConnectionStatusSchema = z.enum([
  "not-configured",
  "test-mode",
  "live",
  "unavailable",
]);
export type PaymentConnectionStatus = z.infer<typeof paymentConnectionStatusSchema>;

/**
 * Payment provider health for the Integrations surface. Absent or failed probes are unknown,
 * mirroring the operator catalogue probe, so a quiet probe never reads as healthy.
 */
export const paymentConnectionHealthSchema = z.enum(["healthy", "degraded", "down", "unknown"]);
export type PaymentConnectionHealth = z.infer<typeof paymentConnectionHealthSchema>;

/**
 * Payment connection state payload returned by the server.
 */
export const paymentConnectionStateSchema = z.object({
  status: paymentConnectionStatusSchema,
  configured: z.boolean(),
  testMode: z.boolean(),
  health: paymentConnectionHealthSchema,
});
export type PaymentConnectionState = z.infer<typeof paymentConnectionStateSchema>;

/**
 * Selectable repository returned from an installation.
 */
export const selectableRepositorySchema = z.object({
  providerRepoId: z.number().int().positive(),
  owner: z.string().min(1),
  name: z.string().min(1),
  fullName: z.string().min(1),
  defaultBranch: z.string().min(1),
  readOnly: z.boolean(),
});
export type SelectableRepository = z.infer<typeof selectableRepositorySchema>;

export const selectableRepositoryListSchema = z.array(selectableRepositorySchema);
export type SelectableRepositoryList = z.infer<typeof selectableRepositoryListSchema>;

/**
 * Request body for connecting a repository without manual SQL.
 */
export const connectRepositoryInputSchema = z.object({
  providerRepoId: z.number().int().positive(),
  installationId: z.number().int().positive(),
  owner: z.string().trim().min(1).max(120),
  name: z.string().trim().min(1).max(120),
  defaultBranch: z.string().trim().min(1).max(120).optional().default("main"),
  readOnly: z.boolean().optional().default(false),
});
export type ConnectRepositoryInput = z.infer<typeof connectRepositoryInputSchema>;

/**
 * Result of connecting a repository.
 */
export const connectRepositoryResultSchema = z.object({
  repositoryId: z.string().uuid(),
  projectId: z.string().uuid(),
  fullName: z.string().min(1),
  installationId: z.number().int().positive(),
  defaultBranch: z.string().min(1),
  readOnly: z.boolean(),
});
export type ConnectRepositoryResult = z.infer<typeof connectRepositoryResultSchema>;
